import { articleTemplateApi, ArticleTemplateDetailRes } from '@/axios/articleTemplate';
import {
  companyApi,
  CompetitorMode,
  CreateOptimizedCompanyArticleReq,
  EntityWordMode,
  WordMode,
} from '@/axios/company';
import ArticleTemplateCombinationEditor, {
  defaultCombinations,
  validateTemplateCombinations,
} from '@/components/Business/ArticleTemplateCombinationEditor';
import CompanySelector from '@/components/Form/CompanySelector';
import ArticleTemplateSelector from '@/components/Form/ArticleTemplateSelector';
import CommonModalFactory from '@/plugins/ModalFactory';
import appStore from '@/stores/app';
import { Checkbox, Col, Flex, Form, Input, InputNumber, Radio, Row, Space, message, notification } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import styles from './batchOptimizeModal.module.less';

export type BatchOptimizeCompany = {
  companyId: string;
  companyName: string;
};

type BatchOptimizeFormValues = {
  companyIds: string | string[];
  autoPublish: boolean;
  coreVisibilityMin: number;
  coreVisibilityMax: number;
  sceneWordMode: WordMode;
  locationWordMode: WordMode;
  entityWordMode: EntityWordMode;
  competitorMode: CompetitorMode;
  competitorRandomCount?: number;
  platforms: string[];
  templateId: number | string;
};

const submitBatchOptimize = async (payload: CreateOptimizedCompanyArticleReq) => {
  return companyApi.createOptimizedArticle(payload);
};

const toOptionalNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const result = Number(value);
  return Number.isFinite(result) ? result : undefined;
};

export const openBatchOptimizeModal = CommonModalFactory<{
  selectedCompanies: BatchOptimizeCompany[];
  allowMultiSelect?: boolean;
}>(
  {
    title: '批量优化',
    width: 1000,
    maskClosable: false,
    styles: {
      body: {
        maxHeight: '500px',
        overflowY: 'auto',
      },
    },
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm<BatchOptimizeFormValues>();
    const [combinations, setCombinations] = useState<ArticleTemplateDetailRes['combinations']>([
      ...defaultCombinations,
    ]);
    const isBatchMode = Boolean(data?.allowMultiSelect);

    const initialCompanyIds = useMemo(() => data?.selectedCompanies?.map((item) => item.companyId) || [], [data]);
    const initialCompanyValue = useMemo(
      () => (isBatchMode ? initialCompanyIds : initialCompanyIds[0]),
      [isBatchMode, initialCompanyIds]
    );
    const defaultCompanyOptions = useMemo(
      () => (data?.selectedCompanies || []).map((item) => ({ label: item.companyName, value: item.companyId })),
      [data]
    );
    const optimizePlatformOptions = useMemo(
      () =>
        (appStore.platformList || [])
          .filter((item) => ['DOUBAO', 'DEEPSEEK', 'YUANBAO'].includes(String(item.platform || '').toUpperCase()))
          .map((item) => ({
            label: item.name,
            value: item.platform,
          })),
      [appStore.platformList]
    );
    const watchedTemplateId = Form.useWatch('templateId', form);
    const competitorMode = Form.useWatch('competitorMode', form);

    useEffect(() => {
      form.setFieldsValue({
        companyIds: initialCompanyValue,
        autoPublish: true,
        coreVisibilityMin: 0,
        coreVisibilityMax: 100,
        sceneWordMode: WordMode.All,
        locationWordMode: WordMode.All,
        entityWordMode: EntityWordMode.One,
        competitorMode: CompetitorMode.None,
        competitorRandomCount: 4,
        platforms: optimizePlatformOptions.map((item) => item.value),
      });
      setCombinations([...defaultCombinations]);
    }, [form, initialCompanyValue, optimizePlatformOptions]);

    useEffect(() => {
      if (!watchedTemplateId) {
        setCombinations([...defaultCombinations]);
        return;
      }
      articleTemplateApi.get(watchedTemplateId).then((detail) => {
        setCombinations(detail.combinations?.length ? detail.combinations : [...defaultCombinations]);
      });
    }, [watchedTemplateId, form]);

    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        const companyNameMap = new Map(defaultCompanyOptions.map((item) => [item.value, item.label]));
        if (values.coreVisibilityMin > values.coreVisibilityMax) {
          message.error('核心词可见度范围不合法');
          throw new Error('核心词可见度范围不合法');
        }
        if (
          values.competitorMode === CompetitorMode.RandomN &&
          (!values.competitorRandomCount || values.competitorRandomCount < 1)
        ) {
          message.error('请填写大于等于1的随机竞品数量n');
          throw new Error('请填写大于等于1的随机竞品数量n');
        }
        validateTemplateCombinations(combinations);
        const rawCompanyIds = Array.isArray(values.companyIds) ? values.companyIds : [values.companyIds];
        const companyIds = rawCompanyIds.map((id) => String(id)).filter((id) => id.trim());
        if (!companyIds.length || companyIds.length !== rawCompanyIds.length) {
          message.error('存在非法公司ID（应为非空字符串），无法提交');
          throw new Error('存在非法公司ID（应为非空字符串），无法提交');
        }
        const platforms = (values.platforms || []).map((item) => String(item).trim()).filter(Boolean);
        if (!platforms.length) {
          message.error('请至少选择一个优化平台');
          throw new Error('请至少选择一个优化平台');
        }
        let normalizedCombinations: CreateOptimizedCompanyArticleReq['combinations'] = [];
        try {
          normalizedCombinations = combinations.map((combination) => ({
            articleCount: combination.articleCount || 1,
            combinationId: toOptionalNumber(combination.combinationId),
            combinationName: combination.combinationName,
            items: (combination.items || []).map((item) => {
              const articleTypeId = String(item.articleTypeId || '').trim();
              const mediaId = Number(item.mediaId);
              if (!articleTypeId || !Number.isFinite(mediaId)) {
                throw new Error('模板组合存在非法的文章类型或媒体ID');
              }
              return {
                articleTypeId,
                id: toOptionalNumber(item.id),
                mediaId,
              };
            }),
          }));
        } catch (error: any) {
          message.error(error?.message || '模板配置数据不合法');
          throw error;
        }
        const payload: CreateOptimizedCompanyArticleReq = {
          autoPublish: values.autoPublish,
          // 这里必须保持 string[]，不要转 number，避免 companyId 精度丢失。
          companyIds,
          competitorMode: values.competitorMode,
          competitorRandomCount:
            values.competitorMode === CompetitorMode.RandomN ? values.competitorRandomCount : undefined,
          coreWordVisibilityMax: values.coreVisibilityMax,
          coreWordVisibilityMin: values.coreVisibilityMin,
          entityWordMode: values.entityWordMode,
          locationWordMode: values.locationWordMode,
          platforms,
          sceneWordMode: values.sceneWordMode,
          combinations: normalizedCombinations,
        };
        const result = await submitBatchOptimize(payload);
        const successCount = result?.successCount ?? 0;
        const failedCount = result?.failedCount ?? 0;
        const failedItems = (result?.results || []).filter((item) => !item.success);
        const singleCompanyId = companyIds[0];
        const singleCompanyName = companyNameMap.get(singleCompanyId) || singleCompanyId;
        const singleFailedItem = (result?.results || []).find((item) => item.companyId === singleCompanyId && !item.success);
        if (failedCount > 0) {
          if (!isBatchMode) {
            const suffix = singleFailedItem?.errorMsg ? `：${singleFailedItem.errorMsg}` : '';
            message.error(`创建失败：【${singleCompanyName}】${suffix}`, 5);
            throw new Error(`single optimize failed: ${singleCompanyId}`);
          }
          const failedDetails = failedItems.map((item) => {
            const companyName = companyNameMap.get(item.companyId) || item.companyId;
            const errorReason = item.errorMsg?.trim() || '未知原因';
            return `【${companyName}】：${errorReason}`;
          });
          notification.open({
            type: 'warning',
            duration: 5,
            message: `创建完成：成功${successCount}家，失败${failedCount}家`,
            description: (
              <div style={{ maxWidth: 720, lineHeight: 1.6 }}>
                {!!failedDetails.length && (
                  <div style={{ marginTop: 8, maxHeight: 280, overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                    {failedDetails.map((detail, index) => (
                      <div key={`${index}-${detail}`}>{`${index + 1}. ${detail}`}</div>
                    ))}
                  </div>
                )}
              </div>
            ),
          });
          return;
        }
        if (!isBatchMode) {
          notification.open({
            type: 'success',
            message: `创建完成：【${singleCompanyName}】已提交优化`,
          });
          return;
        }
        notification.open({
          type: 'success',
          message: `创建完成：成功${successCount}家，失败0家`,
        });
      });
    }, [form, combinations, setOkFn, defaultCompanyOptions, isBatchMode]);

    return (
      <Form
        form={form}
        layout="horizontal"
        labelAlign="right"
        labelWrap
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        className={styles.batchOptimizeForm}
      >
        <Row>
          <Col span={24}>
            <Form.Item
              name="companyIds"
              label="企业"
              rules={[{ required: true, message: '请选择企业' }]}
              extra={isBatchMode ? `已预选 ${initialCompanyIds.length} 个企业` : undefined}
            >
              <CompanySelector multiple={isBatchMode} defaultOptions={defaultCompanyOptions} />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              name="autoPublish"
              label="是否自动发布"
              rules={[{ required: true, message: '请选择是否自动发布' }]}
            >
              <Radio.Group
                options={[
                  { label: '是', value: true },
                  { label: '否', value: false },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              name="platforms"
              label="优化平台"
              rules={[
                {
                  validator: (_, value) =>
                    Array.isArray(value) && value.length > 0
                      ? Promise.resolve()
                      : Promise.reject(new Error('请至少选择一个优化平台')),
                },
              ]}
            >
              <Checkbox.Group options={optimizePlatformOptions} />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item label="使用核心词可见度范围" required>
              <Flex gap={8}>
                <Form.Item name="coreVisibilityMin" noStyle rules={[{ required: true, message: '请输入最小值' }]}>
                  <InputNumber<number>
                    min={0}
                    max={100}
                    formatter={(value) => `${value ?? ''}%`}
                    parser={(value) => Number((value || '').replace('%', ''))}
                    style={{ width: 100 }}
                  />
                </Form.Item>
                <span style={{ display: 'inline-block', width: 24, textAlign: 'center' }}>-</span>
                <Form.Item
                  name="coreVisibilityMax"
                  noStyle
                  rules={[
                    { required: true, message: '请输入最大值' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const min = getFieldValue('coreVisibilityMin');
                        if (typeof min !== 'number' || typeof value !== 'number' || min <= value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('最大值不能小于最小值'));
                      },
                    }),
                  ]}
                >
                  <InputNumber<number>
                    min={0}
                    max={100}
                    formatter={(value) => `${value ?? ''}%`}
                    parser={(value) => Number((value || '').replace('%', ''))}
                    style={{ width: 100 }}
                  />
                </Form.Item>
              </Flex>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item name="sceneWordMode" label="使用场景词" rules={[{ required: true, message: '请选择' }]}>
              <Radio.Group
                options={[
                  { label: '使用全部场景词', value: WordMode.All },
                  { label: '不使用场景词', value: WordMode.None },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item name="locationWordMode" label="使用区位词" rules={[{ required: true, message: '请选择' }]}>
              <Radio.Group
                options={[
                  { label: '使用全部区位词', value: WordMode.All },
                  { label: '不使用区位词', value: WordMode.None },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item name="entityWordMode" label="使用实体词" rules={[{ required: true, message: '请选择' }]}>
              <Radio.Group
                options={[
                  { label: '使用一个实体词', value: EntityWordMode.One },
                  { label: '使用全部实体词', value: EntityWordMode.All },
                  { label: '不使用实体词', value: EntityWordMode.None },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item label="使用竞品" required>
              <Space vertical style={{ width: '100%' }}>
                <Form.Item name="competitorMode" noStyle rules={[{ required: true, message: '请选择' }]}>
                  <Radio.Group>
                    <Space>
                      <Radio value={CompetitorMode.None}>不使用竞品</Radio>
                      <Radio value={CompetitorMode.RandomN}>
                        <Space size={4} align="center">
                          <span>使用随机</span>
                          <Form.Item name="competitorRandomCount" noStyle>
                            <InputNumber
                              min={1}
                              style={{ width: 80 }}
                              placeholder="n"
                              disabled={competitorMode !== CompetitorMode.RandomN}
                            />
                          </Form.Item>
                          <span>个竞品</span>
                        </Space>
                      </Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </Space>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              name="templateId"
              label="使用发文模板"
              extra="选择后可在下方预览并调整模板组合，调整后将使用新的组合进行文章生成。"
            >
              <ArticleTemplateSelector />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <ArticleTemplateCombinationEditor value={combinations} onChange={setCombinations} />
          </Col>
        </Row>
      </Form>
    );
  }
);
